import React from 'react';
import Link from 'next/link';
import clsx from 'clsx';

export const WidgetTab = ({
  active,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }) => (
  <button
    className={clsx(
      'cursor-pointer rounded-md border-0 bg-transparent px-2.5 py-1 text-xs font-bold',
      active ? 'bg-[#eef3fb] text-[#3f6df6]' : 'text-bzgray-600',
    )}
    type="button"
    {...props}
  >
    {children}
  </button>
);

export const WidgetFooterButton = ({
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    className="view-all flex w-full cursor-pointer items-center justify-center gap-[5px] border-0 border-t border-[#eef3fb] bg-transparent px-1 pb-0.5 pt-[11px] text-center font-sans text-[12.5px] font-bold normal-case text-[#3f6df6] disabled:cursor-default disabled:text-[#c2cddd]"
    type="button"
    {...props}
  >
    {children}
  </button>
);

export const WidgetFooterLink = (props: React.ComponentProps<typeof Link>) => (
  <Link
    className="view-all flex w-full cursor-pointer items-center justify-center gap-[5px] border-0 border-t border-[#eef3fb] bg-transparent px-1 pb-0.5 pt-[11px] text-center font-sans text-[12.5px] font-bold normal-case text-[#3f6df6]"
    {...props}
  />
);

export interface WidgetProps {
  icon?: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  headerRight?: React.ReactNode;
  tabs?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  bodyRef?: React.Ref<HTMLDivElement>;
  scrollable?: boolean;
  children: React.ReactNode;
}

export const Widget = ({
  icon,
  title,
  subtitle,
  headerRight,
  tabs,
  footer,
  className,
  bodyClassName,
  bodyRef,
  scrollable = false,
  children,
}: WidgetProps) => (
  <div
    className={clsx(
      'gt-widget flex flex-col rounded-xl bg-white p-4 shadow-[0_1px_2px_rgb(25_41_64_/_6%)]',
      scrollable &&
        'max-h-[440px] overflow-hidden [&_.gt-widget__body]:min-h-0 [&_.gt-widget__body]:max-h-none [&_.gt-widget__body]:flex-auto [&_.gt-widget__body]:overflow-y-auto',
      className,
    )}
  >
    {title || icon || subtitle ? (
      <div className="mb-2 flex items-center gap-2.5 border-b border-[#eef3fb] pb-3">
        {icon ? <span className="flex shrink-0">{icon}</span> : null}
        <div className="flex min-w-0 flex-col">
          {title ? <span className="text-[15px] font-bold leading-tight text-bzgray-900">{title}</span> : null}
          {subtitle ? <span className="text-xs leading-[1.3] text-bzgray-600">{subtitle}</span> : null}
        </div>
        {headerRight ? <span className="ml-auto shrink-0">{headerRight}</span> : null}
      </div>
    ) : null}
    {tabs ? <div className="mb-2 flex gap-1.5">{tabs}</div> : null}
    <div className={clsx('gt-widget__body flex flex-col gap-1', bodyClassName)} ref={bodyRef}>
      {children}
    </div>
    {footer ? <div className="relative z-[1] mt-2 bg-white">{footer}</div> : null}
  </div>
);
